/**
 * Conversions — the cross-vertical contract for "a thing the site
 * owner counts as a win": a purchase, a subscription, a passport
 * renewal, a donation, a newsletter signup.
 *
 * The rule that makes every vertical fit one schema:
 *
 *   **Labels are open, shapes are closed.**
 *
 * A conversion *kind* is any slug the customer declares (`purchase`,
 * `service_completed`, `completed_read`, …). But a kind's *fact* can
 * only ever be one of two shapes:
 *
 *   - `counted` — the event happened; count = 1. Nothing else.
 *   - `money`   — an amount in minor units + ISO-4217 currency.
 *
 * No third shape, no props bag, no metadata passthrough. Open labels
 * are what let a council and a bookshop use the same product; closed
 * shapes are what keep the ingest PII contract
 * (`plans/1.1.0/ingest/00-overview.md`) enforceable — there is no
 * field an email address could ride in on.
 *
 * Sources of conversions (all reduce to the same normalized event):
 *   - the generic webhook (`ConversionWebhookPayloadSchema` — one
 *     required field), for any backend in any vertical;
 *   - provider adapters (Stripe / Polar / Paddle / Creem / Shopify),
 *     which normalize below this seam;
 *   - the pixel (`cw.track()` / `data-track=`) for counted kinds only
 *     — money is webhook-led, never browser-led (trust / coverage /
 *     lifecycle / privacy: see
 *     `plans/1.0.0/cli-and-mcp/07-conversions-and-revenue.md`);
 *   - the `test` provider — same pipeline, rows visibly tagged, so
 *     demos and fixtures flow through the exact production path.
 *
 * Weights are read-layer config (`ConversionKindDef.weight`), never
 * stored on events — re-weighting re-sorts instantly and the event
 * stream stays clean.
 */
import { z } from 'zod';

// ------------------------------------------------------------------
// Fact shapes — closed. Adding a third shape is a product decision
// with a spec, not a field.
// ------------------------------------------------------------------

export const CONVERSION_FACT_SHAPES = ['counted', 'money'] as const;
export type ConversionFactShape = (typeof CONVERSION_FACT_SHAPES)[number];

/**
 * Kind keys are snake_case slugs, 2–40 chars. Open set — customers
 * declare their own. The slug constraint (not free text) keeps keys
 * joinable, URL-safe, and un-smugglable (no emails-as-kinds).
 */
export const ConversionKindKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'kind must be a snake_case slug, 2-40 chars');

export const CONVERSION_PROVIDERS = [
  'webhook', // the generic inbound webhook (any vertical, any backend)
  'pixel', // cw.track() / data-track= — counted kinds only
  'test', // synthetic events; same pipeline, visibly tagged
  'stripe',
  'polar',
  'paddle',
  'creem',
  'shopify',
] as const;
export type ConversionProvider = (typeof CONVERSION_PROVIDERS)[number];

export const ATTRIBUTION_CONFIDENCE = ['exact', 'inferred', 'unknown'] as const;
export type AttributionConfidence = (typeof ATTRIBUTION_CONFIDENCE)[number];

// ------------------------------------------------------------------
// Wire payload — what the generic webhook receives. One required
// field. `.strict()` everywhere: unknown fields are rejected, not
// dropped, so a payload carrying PII fails loudly at the boundary.
// ------------------------------------------------------------------

export const ConversionMoneySchema = z
  .object({
    /** Minor units (pence/cents). Negative allowed for refund-shaped kinds. */
    amount: z.number().int(),
    /** ISO 4217, uppercase. */
    currency: z.string().regex(/^[A-Z]{3}$/, 'currency must be an uppercase ISO-4217 code'),
  })
  .strict();
export type ConversionMoney = z.infer<typeof ConversionMoneySchema>;

export const ConversionWebhookPayloadSchema = z
  .object({
    /** The declared kind this event counts toward. The only required field. */
    kind: ConversionKindKeySchema,
    /** Pixel session ref from `cw.checkoutRef()` — enables exact page attribution. */
    session: z.string().min(8).max(64).optional(),
    /** Money fact — only valid on kinds declared (or auto-declared) as `money`. */
    value: ConversionMoneySchema.optional(),
    /** When it happened; defaults to receipt time. */
    occurred_at: z.string().datetime({ offset: true }).optional(),
    /** Sender's idempotency key (order id, submission ref). Defaults to the event's own uuid. */
    id: z.string().min(1).max(128).optional(),
  })
  .strict();
export type ConversionWebhookPayload = z.infer<typeof ConversionWebhookPayloadSchema>;

// ------------------------------------------------------------------
// Normalized event — every source reduces to this before persistence.
// The fact is a discriminated union: exactly the two closed shapes.
// ------------------------------------------------------------------

export const ConversionFactSchema = z.discriminatedUnion('shape', [
  z.object({ shape: z.literal('counted'), count: z.literal(1) }).strict(),
  z
    .object({
      shape: z.literal('money'),
      amount_minor: z.number().int(),
      currency: z.string().regex(/^[A-Z]{3}$/),
    })
    .strict(),
]);
export type ConversionFact = z.infer<typeof ConversionFactSchema>;

export const ConversionEventSchema = z
  .object({
    conversion_uuid: z.string().regex(/^conv-/),
    org_uuid: z.string().min(1),
    site_uuid: z.string().min(1),

    kind: ConversionKindKeySchema,
    provider: z.enum(CONVERSION_PROVIDERS),
    /** Idempotency key: `(provider, provider_event_id)` is unique on the write side. */
    provider_event_id: z.string().min(1),

    fact: ConversionFactSchema,

    occurred_at: z.string().datetime({ offset: true }),
    ingested_at: z.string().datetime({ offset: true }),

    // Attribution. Normalization records the session ref; resolving it
    // to a page (last-page-of-session join) is the pipeline's
    // enrichment step, which upgrades confidence from 'unknown'.
    session_uuid: z.string().nullable(),
    attributed_path: z.string().nullable(),
    attribution_confidence: z.enum(ATTRIBUTION_CONFIDENCE),
  })
  .strict();
export type ConversionEvent = z.infer<typeof ConversionEventSchema>;

// ------------------------------------------------------------------
// Kind definitions + vertical presets. Presets are seeds, not schema
// — every kind and weight is editable, and "custom" starts empty.
// ------------------------------------------------------------------

export const ConversionKindDefSchema = z
  .object({
    key: ConversionKindKeySchema,
    /** Human label ("Newsletter signup"). */
    label: z.string().min(1).max(40),
    shape: z.enum(CONVERSION_FACT_SHAPES),
    /**
     * Expected value per event, in the template's unit (major currency
     * units or points). Read-layer config — applied at query time,
     * never stored on events. Money kinds carry their own realized
     * amounts, so their weight is unused; keep it 0.
     */
    weight: z.number().nonnegative(),
  })
  .strict();
export type ConversionKindDef = z.infer<typeof ConversionKindDefSchema>;

export const CONVERSION_PRESET_KEYS = ['commerce', 'publisher', 'government', 'custom'] as const;
export type ConversionPresetKey = (typeof CONVERSION_PRESET_KEYS)[number];

export interface ConversionTemplate {
  preset: ConversionPresetKey;
  /** How the site talks about itself in the preset picker ("Shop"). */
  presetLabel: string;
  /** Label for the value lens ("Revenue", "Reader value", …). */
  valueLabel: string;
  /**
   * 'currency' when the template's value is money-denominated (any
   * money kind, or £-denominated weights); 'points' otherwise.
   */
  valueUnit: 'currency' | 'points';
  kinds: ConversionKindDef[];
}

/**
 * Commerce: value is realized revenue only — the counted kinds default
 * to weight 0 so the "Revenue" lens never mixes estimates into actual
 * money. Raising a weight is the customer's explicit call.
 */
const COMMERCE_TEMPLATE: ConversionTemplate = {
  preset: 'commerce',
  presetLabel: 'Shop',
  valueLabel: 'Revenue',
  valueUnit: 'currency',
  kinds: [
    { key: 'purchase', label: 'Purchase', shape: 'money', weight: 0 },
    { key: 'refund', label: 'Refund', shape: 'money', weight: 0 },
    { key: 'signup', label: 'Account signup', shape: 'counted', weight: 0 },
    { key: 'add_to_cart', label: 'Add to cart', shape: 'counted', weight: 0 },
  ],
};

/**
 * Publisher: subscriptions carry real money; audience actions carry
 * expected values in major currency units (a read ≈ 5p of ad/loyalty
 * value — deliberately editable).
 */
const PUBLISHER_TEMPLATE: ConversionTemplate = {
  preset: 'publisher',
  presetLabel: 'Publication',
  valueLabel: 'Reader value',
  valueUnit: 'currency',
  kinds: [
    { key: 'subscription', label: 'Subscription', shape: 'money', weight: 0 },
    { key: 'newsletter_signup', label: 'Newsletter signup', shape: 'counted', weight: 4 },
    { key: 'registration', label: 'Registration', shape: 'counted', weight: 2 },
    { key: 'completed_read', label: 'Completed read', shape: 'counted', weight: 0.05 },
  ],
};

/**
 * Government: no money anywhere — value is task completion, in
 * points. (Cost-avoided-per-completion £ weighting is a natural
 * customer edit; the default stays unitless.)
 */
const GOVERNMENT_TEMPLATE: ConversionTemplate = {
  preset: 'government',
  presetLabel: 'Public service',
  valueLabel: 'Service value',
  valueUnit: 'points',
  kinds: [
    { key: 'service_completed', label: 'Service completed', shape: 'counted', weight: 10 },
    { key: 'form_submitted', label: 'Form submitted', shape: 'counted', weight: 5 },
    { key: 'eligibility_check', label: 'Eligibility check', shape: 'counted', weight: 2 },
  ],
};

const CUSTOM_TEMPLATE: ConversionTemplate = {
  preset: 'custom',
  presetLabel: 'Something else',
  valueLabel: 'Value',
  valueUnit: 'points',
  kinds: [],
};

export const CONVERSION_PRESETS: Record<ConversionPresetKey, ConversionTemplate> = {
  commerce: COMMERCE_TEMPLATE,
  publisher: PUBLISHER_TEMPLATE,
  government: GOVERNMENT_TEMPLATE,
  custom: CUSTOM_TEMPLATE,
};

// ------------------------------------------------------------------
// Value math — the read-layer weighting, shared by the dashboard
// board, the seed script, and (later) the Tinybird pipe parameters.
// ------------------------------------------------------------------

export interface ConversionRollup {
  /** Event count per kind key. */
  counts: Record<string, number>;
  /** Realized money per money-kind key, in minor units (refunds negative). */
  moneyMinor: Record<string, number>;
}

/**
 * Total value of a rollup under a template, in display units (major
 * currency units when `valueUnit === 'currency'`, else points).
 * Money kinds contribute realized amounts; counted kinds contribute
 * `count × weight`.
 */
export function computeConversionValue(
  rollup: ConversionRollup,
  template: ConversionTemplate,
): number {
  let total = 0;
  for (const kind of template.kinds) {
    if (kind.shape === 'money') {
      total += (rollup.moneyMinor[kind.key] ?? 0) / 100;
    } else {
      total += (rollup.counts[kind.key] ?? 0) * kind.weight;
    }
  }
  return total;
}

/** Plain event count across all kinds in the template. */
export function computeConversionCount(
  rollup: ConversionRollup,
  template: ConversionTemplate,
): number {
  return template.kinds.reduce((sum, kind) => sum + (rollup.counts[kind.key] ?? 0), 0);
}

// ------------------------------------------------------------------
// Normalization — the single construction site for conversion events
// (the `toConversionEvent` boundary the ingest plan calls for).
// Pure and deterministic: ids + clock are injected so the worker, the
// seed script, and tests all share it.
// ------------------------------------------------------------------

export const CONVERSION_NORMALIZE_ERRORS = [
  /** `value` sent for a kind declared `counted` — a config mismatch the sender must see. */
  'VALUE_ON_COUNTED_KIND',
  /** A `money` kind arrived without `value`. */
  'MONEY_KIND_REQUIRES_VALUE',
] as const;
export type ConversionNormalizeError = (typeof CONVERSION_NORMALIZE_ERRORS)[number];

export interface NormalizeConversionContext {
  org_uuid: string;
  site_uuid: string;
  provider: ConversionProvider;
  /** Injected id for the new event (`conv-…`). */
  conversion_uuid: string;
  /** Injected receipt clock, ISO-8601. */
  received_at: string;
  /** The site's declared kinds. */
  kinds: readonly ConversionKindDef[];
}

export type NormalizeConversionResult =
  | {
      ok: true;
      event: ConversionEvent;
      /**
       * Present when the payload's kind wasn't declared: unknown kinds
       * are accepted (shape derived from the payload, weight 0) and
       * surfaced to the customer as "new conversion kind arrived —
       * what's it worth to you?". The caller persists this def. Weight
       * 0 keeps auto-declared kinds from moving value totals until a
       * human prices them; typos surface on the page instead of
       * silently 400ing at the sender.
       */
      declaredKind?: ConversionKindDef;
    }
  | { ok: false; error: ConversionNormalizeError };

export function normalizeConversion(
  payload: ConversionWebhookPayload,
  ctx: NormalizeConversionContext,
): NormalizeConversionResult {
  let kindDef = ctx.kinds.find((k) => k.key === payload.kind);
  let declaredKind: ConversionKindDef | undefined;

  if (!kindDef) {
    declaredKind = {
      key: payload.kind,
      label: payload.kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
      shape: payload.value ? 'money' : 'counted',
      weight: 0,
    };
    kindDef = declaredKind;
  }

  if (kindDef.shape === 'counted' && payload.value) {
    return { ok: false, error: 'VALUE_ON_COUNTED_KIND' };
  }
  if (kindDef.shape === 'money' && !payload.value) {
    return { ok: false, error: 'MONEY_KIND_REQUIRES_VALUE' };
  }

  const fact: ConversionFact =
    kindDef.shape === 'money' && payload.value
      ? { shape: 'money', amount_minor: payload.value.amount, currency: payload.value.currency }
      : { shape: 'counted', count: 1 };

  const event: ConversionEvent = {
    conversion_uuid: ctx.conversion_uuid,
    org_uuid: ctx.org_uuid,
    site_uuid: ctx.site_uuid,
    kind: payload.kind,
    provider: ctx.provider,
    provider_event_id: payload.id ?? ctx.conversion_uuid,
    fact,
    occurred_at: payload.occurred_at ?? ctx.received_at,
    ingested_at: ctx.received_at,
    session_uuid: payload.session ?? null,
    attributed_path: null,
    attribution_confidence: 'unknown',
  };

  const result: NormalizeConversionResult = { ok: true, event };
  if (declaredKind) result.declaredKind = declaredKind;
  return result;
}
